import clsx from "clsx"
import Link from "next/link"
import type { ReactNode, Ref } from "react"
import Spinner from "./Spinner"

type Props = {
  type?: React.ButtonHTMLAttributes<HTMLButtonElement>["type"]
  variant?:
    | "primary"
    | "secondary"
    | "dark"
    | "light"
    | "transparent"
    | "destructive"
    | "destructive-soft"
    | "warning"
  size?: "xs" | "sm" | "md" | "lg" | "xl"
  fullWidth?: boolean
  disabled?: boolean
  loading?: boolean
  align?: "center" | "start"
  className?: string
  onClick?: React.MouseEventHandler<HTMLElement>
  href?: string
  target?: React.AnchorHTMLAttributes<HTMLAnchorElement>["target"]
  rel?: React.AnchorHTMLAttributes<HTMLAnchorElement>["rel"]
  children: ReactNode
  ref?: Ref<HTMLButtonElement | HTMLAnchorElement>
} & Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement> &
    React.AnchorHTMLAttributes<HTMLAnchorElement>,
  "type" | "className" | "onClick" | "href" | "target" | "rel" | "children"
>

function Button({
  type = "button",
  variant = "primary",
  size = "md",
  fullWidth = false,
  disabled = false,
  loading = false,
  align = "center",
  className = "",
  onClick,
  href,
  target,
  rel,
  children,
  ref,
  ...rest
}: Props) {
  const isDisabled = disabled || loading

  const classes = clsx(
    "relative flex shrink-0 items-center leading-none tracking-tight transition-transform duration-100 active:not-disabled:scale-[0.97] disabled:cursor-not-allowed",

    "outline-offset-2 focus-visible:outline-2",

    variant === "primary" && [
      "bg-gray-900 text-white outline-gray-900 hover:not-disabled:bg-gray-700 disabled:bg-gray-300 disabled:text-gray-400",

      "dark:bg-white dark:text-gray-900 dark:outline-white dark:disabled:bg-white/40 dark:disabled:text-gray-700 dark:hover:not-disabled:bg-gray-200",
    ],

    variant === "secondary" && [
      "bg-gray-900/6 text-gray-700 outline-gray-900 hover:not-disabled:bg-gray-900/10 hover:not-disabled:text-gray-900 disabled:opacity-60",

      "dark:bg-white/6 dark:text-white dark:outline-white dark:disabled:opacity-50 dark:hover:not-disabled:bg-white/10 dark:hover:not-disabled:text-white",
    ],

    variant === "dark" && [
      "bg-gray-900 text-white outline-gray-900 hover:not-disabled:bg-gray-800 disabled:bg-gray-300 disabled:text-gray-400 dark:bg-gray-950 dark:hover:not-disabled:bg-gray-850",

      "dark:outline-white dark:disabled:bg-gray-900 dark:disabled:text-gray-700 dark:disabled:opacity-50",
    ],

    variant === "light" && [
      "bg-white text-gray-700 outline-gray-900 ring ring-gray-200 ring-inset hover:not-disabled:bg-gray-25 hover:not-disabled:text-gray-900 disabled:bg-white/40 disabled:text-gray-300",

      "dark:outline-white dark:disabled:text-gray-700",
    ],

    variant === "transparent" && [
      "text-gray-700 outline-gray-900 hover:not-disabled:bg-gray-150 disabled:opacity-30",

      "dark:text-white dark:outline-white dark:hover:not-disabled:bg-white/15",
    ],

    variant === "destructive" && [
      "bg-red-500 text-white outline-red-500 hover:not-disabled:bg-red-600 disabled:opacity-30",

      "dark:bg-red-700 dark:outline-red-700 dark:hover:not-disabled:bg-red-600",
    ],

    variant === "destructive-soft" && [
      "bg-red-100 text-red-700 outline-red-500 hover:not-disabled:bg-red-200 disabled:opacity-50",

      "dark:bg-red-500/15 dark:text-red-300 dark:outline-red-600 dark:hover:not-disabled:bg-red-500/25",
    ],

    variant === "warning" && [
      "bg-yellow-200/50 text-yellow-700 outline-yellow-500 hover:not-disabled:bg-yellow-200 disabled:opacity-50",

      "dark:bg-yellow-500/25 dark:text-yellow-300 dark:outline-yellow-600 dark:hover:not-disabled:bg-yellow-500/30",
    ],

    size === "xl" && "h-13 rounded-2xl px-5 font-bold text-base",
    size === "lg" && "h-10 rounded-xl px-4 font-bold text-sm",
    size === "md" && "h-9 rounded-xl px-3 font-bold text-sm",
    size === "sm" && "h-8 rounded-[10px] px-3 font-semibold text-sm",
    size === "xs" && "h-7 rounded-[10px] px-2.5 font-semibold text-sm",

    align === "center" ? "justify-center" : "justify-start",
    fullWidth && "w-full",
    className
  )

  const content = (
    <>
      <span
        className={clsx("flex items-center", {
          "gap-x-1.5": ["xs", "sm", "md"].includes(size),
          "gap-x-2": size === "lg",
          "gap-x-2.5": size === "xl",
          "opacity-0": loading,
        })}
      >
        {children}
      </span>
      {loading && (
        <div className="-translate-x-1/2 -translate-y-1/2 absolute top-1/2 left-1/2">
          <Spinner />
        </div>
      )}
    </>
  )

  if (href) {
    if (disabled) {
      return (
        <button
          type={type}
          className={classes}
          disabled={isDisabled}
          ref={ref as React.Ref<HTMLButtonElement>}
          {...rest}
        >
          {content}
        </button>
      )
    }

    if (!href.startsWith("/")) {
      return (
        <a
          href={href}
          className={classes}
          ref={ref as React.Ref<HTMLAnchorElement>}
          onClick={onClick}
          target={target}
          rel={rel}
          {...rest}
        >
          {content}
        </a>
      )
    }

    return (
      <Link
        className={classes}
        href={href}
        ref={ref as React.Ref<HTMLAnchorElement>}
        onClick={onClick}
        {...rest}
      >
        {content}
      </Link>
    )
  }

  return (
    <button
      type={type}
      onClick={onClick}
      className={classes}
      disabled={isDisabled}
      ref={ref as React.Ref<HTMLButtonElement>}
      {...rest}
    >
      {content}
    </button>
  )
}

export default Button
